import React from 'react';
import styled from 'styled-components';
import { Typography } from '@mic3/platform-ui';

import { set } from 'app/utils/immutable/Immutable';
import { get } from 'app/utils/lo/lo';

import { tasksDefaultFilters, tasksDefaultOrder, tasksFilterDefinitions } from 'app/components/organisms/TasksView/tasksFilterDefinitions';
import processsFilterDefinitions, {
    processesDefaultFilters,
    processesDefaultOrder,
} from 'app/containers/Abox/ProcessesList/ProcessFilters';
import eventsFilterDefinitions, {
    eventsDefaultFilters,
    eventsDefaultOrder,
} from 'app/containers/Stream/Events/EventsListView/eventsFilterDefinitions';

import { getEntitiesListFilterDefinitions, isProcessType, isTaskType } from 'app/utils/entity/entityUtils';
import { TASKLAYERS, PROCESSLAYERS, LAYERS } from 'app/utils/maps/layer/layerUtils';
import { addOperatorsInDefinitions, buildCustomDefinitions } from 'app/utils/classification/classificationUtils';
import moment from 'moment';
import { typeMap } from 'app/config/typesConfig';
import GridDueDate from 'app/components/organisms/GridView/GridRenderers/GridDueDate';
import store from 'store/Store';
import { muiTheme } from 'app/themes/materialUi';

export const widgetTypes = {
    list: 'list',
    iframe: 'iframe',
    map: 'map',
    calendar: 'calendar',
    alive: 'alive',
    module: 'module',
    user: 'user',
    link: 'link',
    userApps: 'userApps',
};

export const permissionMap = {
    [widgetTypes.alive]: 'alive.alive',
    [widgetTypes.list]: 'entity.entity',
    [widgetTypes.map]: 'maps.sa',
    [widgetTypes.calendar]: 'abox.calendar',
    [widgetTypes.module]: {
        Entities: 'entity.entity',
        'Events Monitor': 'mistream.event',
        Boards: 'abox.board',
        Calendar: 'abox.calendar',
        Analytics: ['entity.entity', 'analytics.grafana', 'analytics.superAnalytics'],
    },
    [widgetTypes.user]: {
        tasksByDueDate: 'abox.task',
        tasksByPriority: 'abox.task',
        tasksByCreatedDate: 'abox.task',
        lastUpdatedTasks: 'abox.task',
        userMaps: 'maps.sa',
    },
};

export const getAddWidgetMenu = (profile) => {
    const { isAdmin, permissions } = profile || {};
    const permissionsSet = new Set(permissions || []);
    const canAddModule =
        isAdmin ||
        Object.values(permissionMap[widgetTypes.module])
            .flat()
            .some((permission) => permissionsSet.has(permission));
    const canAddUserSpec =
        isAdmin ||
        Object.values(permissionMap[widgetTypes.user])
            .flat()
            .some((permission) => permissionsSet.has(permission));
    const addWidgetMenu = [
        (isAdmin || permissionsSet.has(permissionMap[widgetTypes.list])) && {
            name: 'List',
            type: widgetTypes.list,
            icon: 'format-list-bulleted-square',
        },
        { name: 'Iframe', type: widgetTypes.iframe, icon: 'charts', iconType: 'af' },
        (isAdmin || permissionsSet.has(permissionMap[widgetTypes.map])) && {
            name: 'Map',
            type: widgetTypes.map,
            icon: 'maps',
            iconType: 'af',
        },
        (isAdmin || permissionsSet.has(permissionMap[widgetTypes.calendar])) && {
            name: 'Calendar',
            type: widgetTypes.calendar,
            icon: 'calendar',
        },
        (isAdmin || permissionsSet.has(permissionMap[widgetTypes.alive])) && {
            name: 'A-Live',
            type: widgetTypes.alive,
            icon: 'messenger',
            iconType: 'af',
        },
        canAddModule && { name: 'Module', type: widgetTypes.module, icon: 'view-module', iconType: 'mdi' },
        { name: 'Link', type: widgetTypes.link, icon: 'link', iconType: 'mdi' },
        { name: 'My Apps', type: widgetTypes.userApps, icon: 'multiple-apps', iconType: 'af', iconColor: muiTheme.colors.background.fields },
        canAddUserSpec && { name: 'User Specific', type: widgetTypes.user, icon: 'account', iconType: 'mdi' },
    ].filter(Boolean);

    return addWidgetMenu;
};

export const getViewId = (entityType) => {
    if (!entityType) {
        return;
    }
    switch (true) {
        case TASKLAYERS.includes(entityType):
            return 'TaskList';
        case PROCESSLAYERS.includes(entityType):
            return 'ProcessesListView';
        case entityType === 'system_event':
            return 'EventsListView';
        default:
            return `EntitiesListView.${entityType}`;
    }
};

export const getRouteLink = (entityType, id) => {
    if (!entityType) {
        return;
    }
    switch (true) {
        case entityType === typeMap.map:
            return id ? `/maps/${id}` : '/maps';
        case entityType === 'integration':
            return id ? `/integration/${id}` : '/integration';
        case TASKLAYERS.includes(entityType):
            return id ? `/abox/task/${id}` : '/abox/tasks';
        case PROCESSLAYERS.includes(entityType):
            return id ? `/abox/process/${id}` : '/abox/processes';
        case entityType === 'system_event':
            return id ? `/events/${id}` : '/events';
        default:
            return id ? `/entities/${entityType}/${id}` : `/entities/${entityType}`;
    }
};

// Define custom breakpoints
export const BREAKPOINTS = {
    '3xl': 2570,
    '2xl': 1930,
    xl: 1450,
    lg: 1210,
    sm: 770,
    xs: 490,
    xxs: 0,
};

// Define columns for each breakpoint
export const COLUMNS = {
    '3xl': 6,
    '2xl': 5,
    xl: 4,
    lg: 3,
    sm: 2,
    xs: 2,
    xxs: 1,
};

export const refreshExceptions = ['link', 'module'];

export const generateGridLayout = (items, columns = 4) => {
    return items.map((widget, index) => ({
        i: widget?.id,
        x: columns < 2 ? 0 : index % columns,
        y: columns < 2 ? index : Math.floor(index / columns) * 1,
        w: 1,
        h: 1,
    }));
};

// Generate layout for the grid
export const generateResponsiveLayout = (widgets) => {
    return Object.keys(BREAKPOINTS).reduce((acc, breakpoint) => {
        acc[breakpoint] = generateGridLayout(widgets, COLUMNS[breakpoint]);
        return acc;
    }, {});
};

export const getBreakpoint = (width) => {
    return (
        Object.keys(BREAKPOINTS)
            .sort((a, b) => BREAKPOINTS[b] - BREAKPOINTS[a])
            .find((key) => width >= BREAKPOINTS[key]) || 'xxs'
    );
};

export const findEmptySpace = (currentLayout, columns) => {
    let maxY = 0;
    let maxX = 0;
    currentLayout.forEach((widget) => {
        if (widget.y + widget.h > maxY) maxY = widget.y + widget.h;
        if (widget.x + widget.w > maxX) maxX = widget.x + widget.w;
    });

    for (let y = 0; y <= maxY; y++) {
        for (let x = 0; x < columns; x++) {
            if (!isPositionOccupied(x, y, currentLayout)) {
                return { x, y };
            }
        }
    }
    return null;
};

export const isPositionOccupied = (x, y, currentLayout) => {
    return currentLayout.some((widget) => y >= widget.y && y < widget.y + widget.h && x >= widget.x && x < widget.x + widget.w);
};

export const getFallbackPosition = (currentLayout, columns) => {
    const lastWidget = currentLayout[currentLayout.length - 1];
    if (!lastWidget) return { x: 0, y: 0, w: 1, h: 1 }; // if adding first time
    const newX = (lastWidget.x + lastWidget.w) % columns;
    const newY = lastWidget.y + Math.floor((lastWidget.x + lastWidget.w) / columns);
    return { x: newX, y: newY, w: 1, h: 1 };
    // }
};

export const widgetDefinitions = {
    entities: {
        widgetType: 'staticWidget',
        header: 'Entities',
        permission: 'entity.entity',
        isSystem: true,
        avatarProps: {
            iconName: 'things',
            iconType: 'af',
            iconColor: 'linear-gradient(90deg, #1248BD 0%, #1E88E5 100%)',
        },
        description:
            'Strong asset management for all aspects of an organisation, from people, endpoints, organisations and custom entities. Module uses a profiling capability adding amazing granularity to each entity with unique attributes, documentation management component, relationship view and a full history.',
        navigationOptions: {
            link: '/entities',
            label: 'GO TO ENTITIES',
        },
    },
    analytics: {
        widgetType: 'staticWidget',
        header: 'Analytics',
        permission: ['entity.entity', 'analytics.grafana', 'analytics.superAnalytics'],
        isSystem: true,
        avatarProps: {
            iconName: 'charts',
            iconType: 'af',
            iconColor: 'linear-gradient(90deg, #00B8A1 0%, #0BB931 100%)',
        },
        description:
            'View all your dashboards, reports and charts in real-time with the ability to create your own. \nPowerful visual representation tool looking at your full operation, from endpoint monitoring to workforce management etc.',
        navigationOptions: {
            link: '/print-reports',
            label: 'GO TO ANALYTICS',
        },
    },
    calendar: {
        widgetType: 'staticWidget',
        header: 'Calendar',
        permission: 'abox.calendar',
        isSystem: true,
        avatarProps: {
            iconName: 'calendar-blank',
            iconType: 'mdi',
            iconColor: 'linear-gradient(90deg, #28AAF3 0.01%, #00B8A1 99.99%)',
        },
        description: `Keep track of your team's tasks and workforce management by seeing tasks assigned and being performed on a daily, weekly and monthly calendar view, with filter capabilities to hone in on specific sites, people and teams.`,
        navigationOptions: {
            link: '/abox/calendar',
            label: 'GO TO CALENDAR',
        },
    },
    boards: {
        widgetType: 'staticWidget',
        header: 'Boards',
        permission: 'abox.board',
        isSystem: true,
        avatarProps: {
            iconName: 'Kanban',
            iconType: 'af',
            iconColor: 'linear-gradient(90deg, #F84219 0.01%, #EE1C80 99.99%)',
        },
        description: `Manage teams and tasks by creating different stages that the tasks go through, giving you another amazing control element in workforce management. \nCreate multiple boards that address different area's or operations in organisation.\nManually track and move tasks through different stages identified. \nDrill into tasks directly, with jump through functionality and granularity linked to specific tasks.`,
        navigationOptions: {
            link: '/abox/kanbanboards',
            label: 'GO TO BOARDS',
        },
    },
    eventsMonitor: {
        widgetType: 'staticWidget',
        header: 'Events Monitor',
        permission: 'mistream.event',
        isSystem: true,
        avatarProps: {
            iconName: 'event-monitor',
            iconType: 'af',
            iconColor: 'linear-gradient(90deg, #9C27B0 0.02%, #7417EB 99.98%)',
        },
        description:
            'Our events monitor displays real-time alarm notifications with their severities that can be actioned on both manually and automatically, filtered according to their preferred view or objectives providing a full contextual history relating to that task or incident including people, documentation, status, action taken, severity level, to name but a few.',
        navigationOptions: {
            link: '/events',
            label: 'GO TO EVENTS MONITOR',
        },
    },
};

export const getWidgetByHeader = (header) => {
    let widget = null;
    Object.values(widgetDefinitions).forEach((value) => {
        if (value.header === header) {
            widget = value;
        }
    });
    return widget;
};

export const getDefaultFilters = (entityType) => {
    if (!entityType) return {};
    switch (true) {
        case TASKLAYERS.includes(entityType):
            return tasksDefaultFilters;
        case PROCESSLAYERS.includes(entityType):
            return processesDefaultFilters;
        case entityType === LAYERS?.event:
            return eventsDefaultFilters;
        default:
            return { active: true };
    }
};

export const getDefaultOrder = (entityType) => {
    if (!entityType) return {};
    switch (true) {
        case TASKLAYERS.includes(entityType):
            return tasksDefaultOrder[0];
        case PROCESSLAYERS.includes(entityType):
            return processesDefaultOrder[0];
        case entityType === LAYERS?.event:
            return eventsDefaultOrder[0];
        default:
            return { field: 'modifiedDate', direction: 'desc nulls last' };
    }
};

export const getAllFilterDefinitions = (entityType, classData, operatorsMap) => {
    if (!entityType) return [];
    switch (true) {
        case TASKLAYERS.includes(entityType):
            return tasksFilterDefinitions;
        case PROCESSLAYERS.includes(entityType):
            return processsFilterDefinitions;
        case entityType === LAYERS?.event:
            return eventsFilterDefinitions;
        default: {
            const customFilters = buildCustomDefinitions(classData, operatorsMap);
            const definitions = getEntitiesListFilterDefinitions(entityType);
            definitions.push(...customFilters);
            return definitions;
        }
    }
};

export const getDefaultFilterDefinitions = (entityType, classData = null, operatorsMap = {}) => {
    let definitions = getAllFilterDefinitions(entityType, classData, operatorsMap);
    definitions = definitions.map((def) => {
        const opValue = operatorsMap?.[def?.properties?.name];
        if (opValue) {
            return set(def, 'properties.opValue', opValue);
        }
        return def;
    });
    definitions = addOperatorsInDefinitions(definitions, operatorsMap);
    definitions = definitions.filter(({ filters }) => filters !== false);
    definitions = definitions.map((def) => {
        const d = { ...def };
        const { name, disabled } = def?.properties || {};
        if (
            ['closedtask', 'opentask', 'closedprocess', 'openprocess'].includes(entityType) &&
            (name === 'statusType' || name === 'status')
        ) {
            d.properties.disabled = true;
        } else if (['system_task', 'system_process'].includes(entityType) && (name === 'statusType' || name === 'status') && disabled) {
            d.properties.disabled = false;
        }
        return d;
    });
    return definitions;
};

export const getMenuItemByName = (name, userProfile) => {
    return getAddWidgetMenu(userProfile).find((m) => m.name === name);
};

export const getDateRange = (type) => {
    if (!['day', 'week', 'month'].includes(type)) return {};

    const  start = moment().startOf(type).toISOString();
    const end = moment().endOf(type).toISOString();
    return { start, end };
};

export const PriorityTypography = styled(Typography)`
    color: ${({ theme, value }) => get(theme.material.colors.priorityTaskColors, `${value}.0`)};
`;

export const canViewWidget = (widgetProps, type) => {
    if(type === widgetTypes.userApps) {
        return true;
    }
    const userProfile = store.getState().user.profile;
    const permissionsSet = new Set(userProfile?.permissions || []);

    if (!permissionMap.hasOwnProperty(type) || userProfile?.isAdmin) return true;
    let modulePermission = '';
    if (type === widgetTypes.module) {
        modulePermission = permissionMap[widgetTypes.module][widgetProps.selectedModule];
    } else if (type === widgetTypes.user) {
        modulePermission = permissionMap[widgetTypes.user][widgetProps.selectedWidget];
    } else if (TASKLAYERS.includes(widgetProps?.entityType)) {
        modulePermission = 'abox.task';
    } else if (PROCESSLAYERS.includes(widgetProps?.entityType)) {
        modulePermission = 'abox.process';
    } else {
        modulePermission = permissionMap[widgetTypes[type]];
    }
    const modPerArray = Array.isArray(modulePermission) ? modulePermission : [modulePermission].filter(Boolean);
    return modPerArray.some((widgetProps) => permissionsSet.has(widgetProps));
};

const StyledWrapper = styled.div`
    display: flex;
    justify-content: end;
    width: auto;
`;
export const renderDueDate = (record) => {
    return (
        <StyledWrapper>
            <GridDueDate record={record} />
        </StyledWrapper>
    );
};

export const normalizeDashboardWidgets = (dashboard) => {
    const dataKey = 'primary.system_dashboard/definition.widgets'
    const widgets = get(dashboard, dataKey, []);

    const normalizedWidgets = widgets.map(w => {
        const widget = { ...w };
        if (widget.type === 'list') {
            const entityType = widget.data?.entityType?.toLowerCase();
            if (isTaskType(entityType)) {
                widget.data.entityType = 'system_task';
            }
            if (isProcessType(entityType)) {
                widget.data.entityType = 'system_process';
            }
        }
        return widget;
    });
    return set(dashboard, dataKey, normalizedWidgets);
}

